import { takeLatest, call, put } from 'redux-saga/effects';
import {
  loadVendors,
  loadVendorsSuccess,
  loadVendorsError,
  saveVendorRequest,
  deleteVendorRequest,
} from './actions';
import {
  listVendors,
  createVendor,
  updateVendor,
  deleteVendor,
} from '@/services/vendors';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateVendors } from '@/containers/posData/actions';
import type { Vendor } from '@/lib/types';
import type { VendorInput } from '@/services/vendors';

function mapVendor(dto: any): Vendor {
  return {
    id: String(dto.id),
    name: dto.name,
    phone: dto.phone,
    email: dto.email || undefined,
    address: dto.address || undefined,
    notes: dto.notes || undefined,
    advanceBalance: Number(dto.advanceBalance ?? 0),
    createdAt: dto.createdAt || new Date().toISOString(),
  };
}

function toInput(vendor: Vendor): VendorInput {
  return {
    name: vendor.name,
    phone: vendor.phone,
    email: vendor.email,
    address: vendor.address,
    notes: vendor.notes,
  };
}

export function* handleLoadVendors(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadVendorsError('No active shop selected'));
      return;
    }
    const raw: any[] = yield call(listVendors, shopId);
    const vendors = raw.map(mapVendor);
    yield put(hydrateVendors(vendors));
    yield put(loadVendorsSuccess(vendors));
  } catch (e: any) {
    yield put(loadVendorsError(e?.message || 'Failed to load vendors'));
  }
}

export function* handleSaveVendor(
  action: ReturnType<typeof saveVendorRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { vendor, isNew } = action.payload;
    if (isNew) {
      yield call(createVendor, shopId, toInput(vendor));
    } else {
      yield call(updateVendor, shopId, Number(vendor.id), toInput(vendor));
    }
    yield put(loadVendors());
  } catch (e: any) {
    yield put(loadVendorsError(e?.message || 'Failed to save vendor'));
  }
}

export function* handleDeleteVendor(
  action: ReturnType<typeof deleteVendorRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deleteVendor, shopId, Number(action.payload.id));
    yield put(loadVendors());
  } catch (e: any) {
    yield put(loadVendorsError(e?.message || 'Failed to delete vendor'));
  }
}

export default function* saga() {
  yield takeLatest(loadVendors.type, handleLoadVendors);
  yield takeLatest(saveVendorRequest.type, handleSaveVendor);
  yield takeLatest(deleteVendorRequest.type, handleDeleteVendor);
}
